import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  createRecapResult,
  getCorrectedRecapFieldNames,
  getTrustedOnboardingProfileFields,
  getUserProps,
  mapOnboardingDetails,
  setUserProps,
  validateOnboardingDetailsFields,
} from '@pharmaplan/common';

import { useAppDispatch } from '../useAppDispatch';
import { useAppSelector } from '../useAppSelector';

import { createOnboardingClientTools } from '../../components/SignupFlow/AiCompanion/clientTools';
import { toElevenLabsClientTools } from '../../components/SignupFlow/AiCompanion/elevenLabsClientTools';
import { validateManualProfessionalProfile } from '../../components/SignupFlow/AiCompanion/manualProfileValidation';
import {
  AiCompanionSessionContext,
  OnboardingRepository,
  RecapResult,
  SaveOnboardingDetailsFields,
} from '../../components/SignupFlow/AiCompanion/types';
import useAiDocumentUpload from './useAiDocumentUpload';
import useAiOnboardingState from './useAiOnboardingState';
import useAiProfileSubmission from './useAiProfileSubmission';
import { buildWorkflowDynamicVariables } from '../../components/SignupFlow/AiCompanion/workflowState';

interface IUseAiCompanionController {
  getConversationId: () => string | null;
  switchToManualMode: () => void;
  sessionContext: AiCompanionSessionContext;
  signupRequestId: string;
}

const useAiCompanionController = ({
  getConversationId,
  switchToManualMode,
  sessionContext,
  signupRequestId,
}: IUseAiCompanionController) => {
  const dispatch = useAppDispatch();
  const userProps = useAppSelector(getUserProps);
  const {
    repository,
    snapshot,
    verifyPharmacistLicense,
  } = useAiOnboardingState({
    signupRequestId,
  });
  const repositoryRef = useRef<OnboardingRepository>(repository);
  const trustedProfileFieldsRef = useRef(
    getTrustedOnboardingProfileFields(sessionContext),
  );
  const pendingRecapRef = useRef<((result: RecapResult) => void) | null>(null);
  const [error, setError] = useState('');
  const [isRecapOpen, setIsRecapOpen] = useState(false);
  const [isVoiceMode, setIsVoiceMode] = useState(true);
  const [pendingClientToolCount, setPendingClientToolCount] = useState(0);
  const documentUpload = useAiDocumentUpload({
    signupRequestId,
  });
  const submitFinalProfile = useAiProfileSubmission({
    getConversationId,
    getProfilePictureFile: documentUpload.getProfilePictureFile,
  });

  repositoryRef.current = repository;
  trustedProfileFieldsRef.current = getTrustedOnboardingProfileFields(
    sessionContext,
  );

  const handleProviderMutedChange = useCallback((isMuted: boolean) => {
    setIsVoiceMode(!isMuted);
  }, []);

  const getDynamicVariables = useCallback(
    async () =>
      buildWorkflowDynamicVariables(sessionContext),
    [sessionContext],
  );

  const openRecap = useCallback(async () => {
    setIsRecapOpen(true);

    return new Promise<RecapResult>((resolve) => {
      pendingRecapRef.current?.(createRecapResult(false));
      pendingRecapRef.current = resolve;
    });
  }, []);

  const updateProfileNames = useCallback((profileNames: {
    firstName: string;
    lastName: string;
  }) => {
    trustedProfileFieldsRef.current = {
      ...trustedProfileFieldsRef.current,
      ...profileNames,
    };
    dispatch(setUserProps({
      ...userProps,
      ...profileNames,
    }));
  }, [dispatch, userProps]);

  const handleClientToolStarted = useCallback(() => {
    setPendingClientToolCount((currentCount) =>
      currentCount + 1);
  }, []);

  const handleClientToolFinished = useCallback(() => {
    setPendingClientToolCount((currentCount) =>
      Math.max(0, currentCount - 1));
  }, []);

  const clientTools = useMemo(() => {
    const onboardingClientTools = createOnboardingClientTools({
      getRepository: () =>
        repositoryRef.current,
      openDocumentUpload: documentUpload.openDocumentUpload,
      openRecap,
      signupRequestId,
      submitFinalProfile,
      switchToManualMode,
      getTrustedProfileFields: () =>
        trustedProfileFieldsRef.current,
      updateProfileNames,
      validateProfile: validateManualProfessionalProfile,
      verifyPharmacistLicense,
    });

    return toElevenLabsClientTools(onboardingClientTools, {
      onClientToolFinished: handleClientToolFinished,
      onClientToolStarted: handleClientToolStarted,
    });
  }, [
    documentUpload.openDocumentUpload,
    handleClientToolFinished,
    handleClientToolStarted,
    openRecap,
    signupRequestId,
    submitFinalProfile,
    switchToManualMode,
    updateProfileNames,
    verifyPharmacistLicense,
  ]);

  const resolveRecap = useCallback((result: RecapResult) => {
    setIsRecapOpen(false);
    pendingRecapRef.current?.(result);
    pendingRecapRef.current = null;
  }, []);

  const handleConfirmRecap = useCallback(
    async (profile: SaveOnboardingDetailsFields) => {
      let validationErrors: string[];

      try {
        validationErrors = await validateOnboardingDetailsFields(profile);

        if (validationErrors.length === 0) {
          validationErrors = await validateManualProfessionalProfile(profile);
        }
      } catch {
        return ['input: Unable to validate the supplied details'];
      }

      if (validationErrors.length > 0) {
        return validationErrors;
      }

      try {
        const currentRepository = repositoryRef.current;
        const currentSnapshot = await currentRepository.getSnapshot();
        const correctedFieldNames = getCorrectedRecapFieldNames(
          currentSnapshot.onboardingDetails,
          profile,
        );

        await currentRepository.saveOnboardingDetails(
          mapOnboardingDetails(profile),
        );
        resolveRecap(createRecapResult(true, correctedFieldNames));

        return [];
      } catch {
        return ['input: Unable to save the supplied details'];
      }
    },
    [resolveRecap],
  );

  useEffect(
    () =>
      () => {
        pendingRecapRef.current?.(createRecapResult(false));
        pendingRecapRef.current = null;
      },
    [],
  );

  return {
    clientTools,
    documentUploadDialogProps: documentUpload.dialogProps,
    getDynamicVariables,
    handleConfirmRecap,
    handleProviderMutedChange,
    isClientToolProcessing: pendingClientToolCount > 0,
    isRecapOpen,
    isVoiceMode,
    setError,
    setIsVoiceMode,
    snapshot,
    visibleError: error,
  };
};

export default useAiCompanionController;
